import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Clock,
  Plus,
  Eye,
  Download,
  ArrowsClockwise,
  CalendarBlank,
  User,
  CheckCircle,
  PaperPlaneTilt,
  Export,
  Timer,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const STATUS_COLORS = {
  draft: 'bg-slate-100 text-slate-700',
  submitted: 'bg-blue-100 text-blue-700',
  approved: 'bg-emerald-100 text-emerald-700',
  exported: 'bg-purple-100 text-purple-700',
};

export const TimesheetsPage = () => {
  const { user, api, isSupervisorPlus } = useAuth();
  const navigate = useNavigate();
  const [timesheets, setTimesheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  
  // Create form
  const [weekEnding, setWeekEnding] = useState(() => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const daysUntilSunday = dayOfWeek === 0 ? 0 : 7 - dayOfWeek;
    const nextSunday = new Date(today);
    nextSunday.setDate(today.getDate() + daysUntilSunday);
    return nextSunday.toISOString().split('T')[0];
  });

  const fetchTimesheets = useCallback(async () => {
    setLoading(true);
    try {
      let url = '/timesheets?limit=100';
      if (statusFilter !== 'all') {
        url += `&status=${statusFilter}`;
      }
      const response = await api.get(url);
      setTimesheets(response.data?.timesheets || []);
    } catch (error) {
      console.error('Failed to fetch timesheets:', error);
      toast.error('Failed to load timesheets');
    } finally {
      setLoading(false);
    }
  }, [api, statusFilter]);

  useEffect(() => {
    fetchTimesheets();
  }, [fetchTimesheets]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      const response = await api.post('/timesheets', {
        week_ending: weekEnding,
        employee_id: user.id,
        employee_name: user.name,
        station: user.station || '',
        department: user.department || 'substation',
        status: 'draft'
      });
      
      toast.success('Timesheet created');
      setShowCreateDialog(false);
      navigate(`/timesheets/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create timesheet');
    } finally {
      setCreating(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'draft': return <Timer size={14} />;
      case 'submitted': return <PaperPlaneTilt size={14} />;
      case 'approved': return <CheckCircle size={14} />;
      case 'exported': return <Export size={14} />;
      default: return null;
    }
  };

  return (
    <div className="p-6 lg:p-10 animate-fade-in" data-testid="timesheets-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Clock size={28} weight="fill" className="text-orange-600" />
            Timesheets
          </h1>
          <p className="text-slate-600 mt-1">Manage your weekly timesheets</p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} data-testid="create-timesheet-btn">
          <Plus size={18} className="mr-2" />
          New Timesheet
        </Button>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex gap-4 items-center">
            <div>
              <label className="text-sm font-medium text-slate-600 mb-1 block">Status</label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40" data-testid="status-filter">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="submitted">Submitted</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="exported">Exported</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" onClick={fetchTimesheets} disabled={loading} className="mt-6">
              <ArrowsClockwise size={16} className={loading ? 'animate-spin' : ''} />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Timesheets Table */}
      <Card>
        <CardHeader>
          <CardTitle>Your Timesheets</CardTitle>
          <CardDescription>
            {timesheets.length} timesheet{timesheets.length !== 1 ? 's' : ''} found
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <ArrowsClockwise size={32} className="animate-spin text-slate-400" />
            </div>
          ) : timesheets.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Clock size={48} className="mx-auto mb-4 text-slate-300" />
              <p>No timesheets found</p>
              <Button variant="outline" className="mt-4" onClick={() => setShowCreateDialog(true)}>
                Create Your First Timesheet
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Week Ending</TableHead>
                  <TableHead>Employee</TableHead>
                  <TableHead>Station</TableHead>
                  <TableHead className="text-right">Total Hours</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {timesheets.map((ts, index) => (
                  <TableRow key={ts.id} data-testid={`timesheet-row-${index}`}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <CalendarBlank size={16} className="text-slate-400" />
                        {new Date(ts.week_ending).toLocaleDateString('en-US', {
                          month: 'short', day: 'numeric', year: 'numeric'
                        })}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <User size={16} className="text-slate-400" />
                        {ts.employee_name}
                      </div>
                    </TableCell>
                    <TableCell>{ts.station || '-'}</TableCell>
                    <TableCell className="text-right font-medium">
                      {(ts.total_hours || 0).toFixed(1)}h
                    </TableCell>
                    <TableCell>
                      <Badge className={`${STATUS_COLORS[ts.status]} flex items-center gap-1 w-fit`}>
                        {getStatusIcon(ts.status)}
                        {ts.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => navigate(`/timesheets/${ts.id}`)}
                        >
                          <Eye size={14} className="mr-1" />
                          View
                        </Button>
                        {ts.status === 'approved' && isSupervisorPlus() && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={async () => {
                              try {
                                const response = await api.post(`/timesheets/${ts.id}/export/xlsx`, {}, {
                                  responseType: 'blob'
                                });
                                const blob = new Blob([response.data], { 
                                  type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
                                });
                                const url = window.URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = `Timesheet_${ts.employee_name.replace(' ', '_')}_${ts.week_ending}.xlsx`;
                                document.body.appendChild(a);
                                a.click();
                                window.URL.revokeObjectURL(url);
                                document.body.removeChild(a);
                                toast.success('Timesheet exported');
                                fetchTimesheets();
                              } catch (error) {
                                toast.error('Export failed');
                              }
                            }}
                          >
                            <Download size={14} className="mr-1" />
                            Export
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Timesheet</DialogTitle>
            <DialogDescription>
              Start a new weekly timesheet
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <label className="text-sm font-medium text-slate-600 mb-2 block">Week Ending (Sunday)</label>
            <Input
              type="date"
              value={weekEnding}
              onChange={(e) => setWeekEnding(e.target.value)}
              data-testid="week-ending-input"
            />
            <p className="text-xs text-slate-500 mt-2">
              Timesheet will cover Monday through Sunday
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={creating}>
              {creating ? 'Creating...' : 'Create Timesheet'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TimesheetsPage;
